import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  FlatList,
  RefreshControl,
  Animated,
  Platform,
  Alert,
  ActivityIndicator,
  Modal,
  TextInput,
  Image,
  Switch,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import NetInfo from '@react-native-community/netinfo';
// Removed unused imports to clean up code (e.g., DateTimePicker, ERPApiService, SafeAreaView - relying on local state for now)

// ============================================
// Brand Colors
// ============================================
const BRAND = {
  blue: '#0e4e96',
  canary: '#fdb913',
  mint: '#19abb5',
  bg: '#f8fafc',
  text: '#0f172a',
  sub: '#475569',
  line: '#e2e8f0',
  white: '#ffffff',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
};

// ============================================
// Module Configuration
// ============================================
const MODULES_CONFIG = {
  crm: {
    id: 'crm',
    name: 'CRM',
    icon: 'account-group',
    color: '#3B82F6',
    description: 'Customer Relationship Management',
    screens: {
      deals: { title: 'CRM Deals', icon: 'briefcase' },
      clients: { title: 'Clients', icon: 'account-group' },
      dealDetail: { title: 'Deal Details', icon: 'file-document' },
      activities: { title: 'Activities', icon: 'calendar' },
      addDeal: { title: 'Add Deal', icon: 'plus-circle' },
      addClient: { title: 'Add Client', icon: 'account-plus' },
    }
  },
  inventory: {
    id: 'inventory',
    name: 'Inventory',
    icon: 'package-variant',
    color: '#10B981',
    description: 'Stock and Warehouse Management',
    screens: {
      products: { title: 'Products', icon: 'package' },
      addProduct: { title: 'Add Product', icon: 'package-plus' },
      reports: { title: 'Reports', icon: 'file-chart' },
    }
  },
  finance: {
    id: 'finance',
    name: 'Finance',
    icon: 'cash-multiple',
    color: '#76B82D',
    description: 'Financial Management & Accounting',
    screens: {
      transactions: { title: 'Transactions', icon: 'cash' },
      addTransaction: { title: 'Add Transaction', icon: 'cash-plus' },
      reports: { title: 'Finance Reports', icon: 'file-chart' },
    }
  },
  hr: {
    id: 'hr',
    name: 'Human Resources',
    icon: 'account-group',
    color: '#8B5CF6',
    description: 'Employee & Payroll Management',
    screens: {
      employees: { title: 'Employees', icon: 'account' },
      addEmployee: { title: 'Add Employee', icon: 'account-plus' },
      attendance: { title: 'Attendance', icon: 'calendar-check' },
      payroll: { title: 'Payroll', icon: 'cash' },
      leave: { title: 'Leave', icon: 'beach' },
    }
  },
  production: {
    id: 'production',
    name: 'Production',
    icon: 'factory',
    color: '#EF4444',
    description: 'Manufacturing & Operations',
    screens: {
      output: { title: 'Production Output', icon: 'chart-line' },
      addProduction: { title: 'Add Production', icon: 'plus-circle' },
      reports: { title: 'Production Reports', icon: 'file-chart' },
    }
  }
};

// ============================================
// Helper Functions
// ============================================
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '$0.00';
  return `$${parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
};

const getStageInfo = (stage) => {
  const stages = {
    lead: { name: 'Lead', color: BRAND.info, icon: 'flag-outline' },
    qualified: { name: 'Qualified', color: BRAND.warning, icon: 'check-circle-outline' },
    proposal: { name: 'Proposal', color: BRAND.canary, icon: 'file-document-outline' },
    negotiation: { name: 'Negotiation', color: BRAND.mint, icon: 'handshake-outline' },
    won: { name: 'Won', color: BRAND.success, icon: 'trophy-outline' },
    lost: { name: 'Lost', color: BRAND.danger, icon: 'close-circle-outline' },
  };
  return stages[stage] || { name: 'Unknown', color: BRAND.sub, icon: 'help-circle-outline' };
};

// Status colors for production records
const STATUS_COLORS = {
  completed: BRAND.success,
  delayed: BRAND.warning,
  'in-progress': BRAND.info,
  active: BRAND.success,
  inactive: BRAND.danger,
};

// ============================================
// Mock Data Generators
// ============================================
const generateMockDeals = (count) => Array.from({ length: count }, (_, i) => ({
  id: `deal-${i + 1}`,
  title: `Deal ${i + 1}`,
  company: `Company ${i + 1}`,
  contact_name: `Contact ${i + 1}`,
  contact_email: `contact${i + 1}@company.com`,
  contact_phone: `+1 555-${1000 + i}`,
  stage: ['lead', 'qualified', 'proposal', 'negotiation', 'won'][i % 5],
  value: Math.floor(Math.random() * 100000) + 50000,
  probability: Math.floor(Math.random() * 40) + 30,
  priority: ['low', 'medium', 'high'][i % 3],
  status: i % 4 === 0 ? 'active' : 'in-progress',
  due_date: new Date(Date.now() + (i + 1) * 86400000).toISOString(),
  created_at: new Date().toISOString(),
  tags: i % 3 ? 
      ['urgent', 'enterprise', 'new'][i % 3] : [],
}));

const generateMockProducts = (count) => Array.from({ length: count }, (_, i) => ({
  id: `product-${i + 1}`,
  name: `Product ${i + 1}`,
  sku: `SKU-${1000 + i}`,
  category: ['Electronics', 'Clothing', 'Food', 'Office'][i % 4],
  quantity: Math.floor(Math.random() * 500) + 50,
  price: Math.floor(Math.random() * 500) + 50,
  cost: Math.floor(Math.random() * 300) + 30,
  status: i % 10 === 0 ? 'low-stock' : 'in-stock',
  location: `WH-${Math.floor(Math.random() * 5) + 1}`,
  last_updated: new Date(Date.now() - i * 3600000).toISOString(),
}));

const generateMockTransactions = (count) => Array.from({ length: count }, (_, i) => ({
  id: `trans-${i + 1}`,
  type: i % 3 === 0 ? 'income' : 'expense',
  category: ['Sales', 'Supplies', 'Salary', 'Utilities'][i % 4],
  description: `Transaction ${i + 1}`,
  amount: Math.floor(Math.random() * 5000) + 100,
  date: new Date(Date.now() - i * 86400000).toISOString(),
  status: i % 5 === 0 ? 'pending' : 'completed',
  reference: `REF-${1000 + i}`,
}));

const generateMockEmployees = (count) => Array.from({ length: count }, (_, i) => ({
  id: `emp-${i + 1}`,
  name: `Employee ${i + 1}`,
  email: `employee${i + 1}@company.com`,
  position: ['Manager', 'Developer', 'Sales', 'HR'][i % 4],
  department: ['IT', 'Sales', 'HR', 'Operations'][i % 4],
  status: i % 8 === 0 ? 'inactive' : 'active',
  salary: Math.floor(Math.random() * 80000) + 40000,
  hire_date: new Date(Date.now() - (i + 1) * 86400000 * 30).toISOString(),
}));

const generateMockProduction = (count) => Array.from({ length: count }, (_, i) => ({
  id: `prod-${i + 1}`,
  product: `Product ${i + 1}`,
  quantity: Math.floor(Math.random() * 1000) + 100,
  machine: `Machine ${i % 5 + 1}`,
  operator: `Operator ${i % 10 + 1}`,
  shift: ['Morning', 'Evening', 'Night'][i % 3],
  quality_score: Math.floor(Math.random() * 20) + 80,
  start_time: new Date(Date.now() - i * 3600000).toISOString(),
  end_time: new Date(Date.now() - i * 3600000 + 1800000).toISOString(),
  status: i % 8 === 0 ? 'delayed' : 'completed',
}));

// ============================================
// ERP Module Detail Component
// ============================================
const ERPModuleDetail = ({ navigation, route }) => {
  // Safely handle route params with defaults
  const params = route?.params || {};
  const {
    moduleId = 'crm',
    screen = 'deals',
    dealId,  // Used for deal details screen
    ...otherParams
  } = params;

  // Get module configuration with fallbacks
  const moduleConfig = MODULES_CONFIG[moduleId] || MODULES_CONFIG.crm;
  const screenConfig = moduleConfig.screens[screen] || {
    title: 'Details',
    icon: 'information',
  };

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  // Data states
  const [deals, setDeals] = useState([]);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [productionRecords, setProductionRecords] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  // Modal states
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Form states
  const [dealForm, setDealForm] = useState({
    title: '',
    company: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    stage: 'lead',
    value: '',
    probability: '30',
    priority: 'medium',
    due_date: new Date(),
    notes: '',
  });

  const [productForm, setProductForm] = useState({
    name: '',
    sku: '',
    category: '',
    quantity: '',
    price: '',
    cost: '',
  });

  const [transactionForm, setTransactionForm] = useState({
    type: 'expense',
    category: '',
    description: '',
    amount: '',
    date: new Date(),
    reference: '',
  });

  // Animations
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;

  // Initialize data
  useEffect(() => {
    loadScreenData();
  }, [moduleId, screen]);

  // Load data based on module and screen
  const loadScreenData = async () => {
    setIsLoading(true);
    try {
      console.log(`🚀 Loading ${moduleConfig.name} - ${screenConfig.title}...`);

      // Load data based on module and screen
      switch (moduleId) {
        case 'crm':
          if (screen === 'deals' || screen === 'dealDetail') {
            if (dealId) {
              const deal = generateMockDeals(1)[0];
              deal.id = dealId;
              setSelectedDeal(deal);
            } else {
              setDeals(generateMockDeals(20));
            }
          } else if (screen === 'clients') {
            setDeals(generateMockDeals(15));
          }
          break;
        case 'inventory':
          if (screen === 'products') {
            setProducts(generateMockProducts(25));
          } else if (screen === 'addProduct') {
            // Initialize product form if needed
          if (!productForm.name) {
             setProductForm({
               name: `New Product ${Math.floor(Math.random() * 1000)}`,
               sku: `NEW-${Math.floor(Math.random() * 1000)}`,
             });
          }
          }
          break;
        case 'finance':
          if (screen === 'transactions') {
            setTransactions(generateMockTransactions(30));
          }
          break;
        case 'hr':
          if (screen === 'employees') {
            setEmployees(generateMockEmployees(20));
          }
          break;
        case 'production':
          if (screen === 'output') {
            setProductionRecords(generateMockProduction(25));
          }
          break;
        default:
          console.warn(`Unknown screen type: ${screen}`);
          break;
      }

      // If the dealId changes, reload the deal (handled by 'deal-detail' case mostly)
      if (dealId && selectedDeal?.id !== dealId) {
          const deal = generateMockDeals(1)[0];
          deal.id = dealId;
          setSelectedDeal(deal);
      }

    } catch (error) {
      console.error('❌ Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refresh
  const onRefresh = async () => {
    setRefreshing(true);
    await loadScreenData();
    setRefreshing(false);
  };

  // Data filtering and sorting
  const getFilteredData = () => {
    let data = [];
    let filtered = [];

    switch (moduleId) {
      case 'crm':
      case 'sales':
        data = deals || [];
        break;
      case 'inventory':
        data = products || [];
        break;
      case 'finance':
        data = transactions || [];
        break;
      case 'hr':
        data = employees || [];
        break;
      case 'production':
        data = productionRecords || [];
        break;
      default:
        data = [];
    }

    // Apply search filter
    if (searchQuery) {
      filtered = data.filter(item => {
        if (!item) return false;
        const searchStr = searchQuery.toLowerCase();
        // Generic search
        const keys = Object.keys(item);
        return keys.some(key => {
            const value = item[key];
            if (typeof value === 'string') {
              return value.toLowerCase().includes(searchStr);
            }
            if (typeof value === 'object' && value !== null) {
                return value.name && typeof value.name === 'string' && String(value.name ?? '').toLowerCase().includes(searchStr);
            }
            return false;
        });
      });
    } else {
      filtered = [...data];
    }

    // Apply status filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(item => item?.status === activeFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (!a || !b) return 0;

      switch (sortBy) {
        case 'recent':
          const dateA = new Date(a.created_at || a.date || 0);
          const dateB = new Date(b.created_at || b.date || 0);
          return dateB - dateA;
        case 'name':
          const nameA = (a.name || a.company || a.description || '').toLowerCase();
          const nameB = (b.name || b.company || b.description || '').toLowerCase();
          return nameA.localeCompare(nameB);
        case 'value':
          const valueA = a.value || a.amount || a.quantity || 0;
          const valueB = b.value || b.amount || b.quantity || 0;
          return valueB - valueA;
        default:
          return 0;
      }
    });

    return filtered;
  };

  // Render functions for different screens

  // 1. List Views

  const renderCRMDeals = () => {
    const filteredDeals = getFilteredData();

    return (
      <View style={styles.listContainer}>
        <FlatList
          data={filteredDeals}
          renderItem={({ item }) => {
            if (!item) return null;

            const stageInfo = getStageInfo(item.stage);

            return (
              <TouchableOpacity
                style={styles.dealCard}
                onPress={() => navigation.navigate('ERPModuleDetail', {
                  moduleId: 'crm',
                  screen: 'dealDetail',
                  dealId: item.id,
                  title: 'Deal Details',
                })}
                activeOpacity={0.8}
              >
                <View style={styles.dealHeader}>
                  <View style={styles.dealTitleContainer}>
                    <Text style={styles.dealTitle} numberOfLines={1}>{item.title || 'Untitled Deal'}</Text>
                    <MaterialCommunityIcons name={stageInfo.icon} color={stageInfo.color} size={20} />
                  </View>
                  <Text style={styles.dealSubtitle}>{item.company || 'No Company'}</Text>
                </View>

                <View style={styles.dealValue}>
                  <Text style={styles.dealValueText}>{formatCurrency(item.value)}</Text>
                </View>
              </TouchableOpacity>
            );
          }}
          keyExtractor={item => item.id}
          ListEmptyComponent={() => (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="briefcase-off" size={64} color={BRAND.sub} />
              <Text style={styles.emptyStateText}>No deals found</Text>
              <Text style={styles.emptyStateSubText}>Try changing your filters or check network</Text>
            </View>
          )}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={moduleConfig.color} />}
        />
      </View>
    );
  };

  const renderInventoryProducts = () => {
    const filteredProducts = getFilteredData();

    return (
      <View style={styles.listContainer}>
        <FlatList
          data={filteredProducts}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.productCard}
              onPress={() => {
                setSelectedProduct(item);
                setShowDetailsModal(true);
              }}
            >
              <View style={styles.productHeader}>
                <MaterialCommunityIcons name="cube-outline" color={moduleConfig.color} size={24} />
              </View>
              <View style={styles.productContent}>
                <Text style={styles.productTitle}>{item.name}</Text>
                <Text style={styles.productSKU}>{item.sku}</Text>
                <View style={styles.productMeta}>
                  <Text style={[
                    styles.productStock,
                    (item.status === 'low-stock' && styles.productLowStock)
                  ]}>
                    <Text>{item.status === 'in-stock' ? `${item.quantity} in stock` : 'Out of stock'}</Text>
                  </Text>
                  <Text style={styles.productLocation}>{item.location}</Text>
                </View>
                <Text style={styles.productPrice}>{formatCurrency(item.price)}</Text>
              </View>
            </TouchableOpacity>
          )}
          keyExtractor={item => item.id}
          ListEmptyComponent={() => (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="package-variant" size={64} color={BRAND.sub} />
              <Text style={styles.emptyStateText}>No products found</Text>
            </View>
          )}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={moduleConfig.color} />}
        />
      </View>
    );
  };

  const renderFinanceTransactions = () => {
    const filteredTransactions = getFilteredData();

    return (
      <View style={styles.listContainer}>
        <FlatList
          data={filteredTransactions}
          renderItem={({ item }) => (
            <View style={styles.transactionCard}>
              <View style={[
                styles.transactionTypeIndicator,
                item.type === 'income' && styles.incomeColor
              ]}>
                <MaterialCommunityIcons name={item.type === 'income' ? 'arrow-down' : 'arrow-up'} color={item.type === 'income' ? BRAND.success : BRAND.danger} size={20} />
              </View>
              <View style={styles.transactionDetails}>
                <Text style={styles.transactionDescription}>{item.description}</Text>
                <View style={styles.transactionMeta}>
                  <Text style={styles.transactionCategory}>{item.category}</Text>
                  <Text style={styles.transactionAmount}>{formatCurrency(item.amount)}</Text>
                  <Text style={[styles.transactionStatus, item.status === 'completed' ? styles.transactionStatusCompleted : null]}>
                    {item.status ? String(item.status ?? '').toUpperCase() : null}
                  </Text>
                </View>
                <Text style={styles.transactionDate}>{new Date(item.date).toLocaleDateString()}</Text>
              </View>
            </View>
          )}
          keyExtractor={item => item.id}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={moduleConfig.color} />}
        />
      </View>
    );
  };

  const renderProductionOutput = () => {
    const filtered = getFilteredData();

    return (
      <View style={styles.listContainer}>
        <FlatList
          data={filtered}
          renderItem={({ item }) => (
            <View style={styles.productionCard}>
              <View style={styles.productionHeader}>
                <Text style={styles.productTitle}>{item.product}</Text>
                <Text style={styles.productionMeta}>{item.quantity} units</Text>
                <Text style={[
                  styles.statusBadge,
                  { backgroundColor: STATUS_COLORS[item.status] || BRAND.info }
                ]}>
                  {item.status || 'Unknown'}
                </Text>
              </View>
            </View>
          )}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={moduleConfig.color} />}
        />
      </View>
    );
  };

  // 2. Detail Views

  const renderDealDetail = () => {
    const deal = deals.find(d => d.id === dealId) || selectedDeal;
    if (!deal) return null;

    return (
      <ScrollView style={styles.detailContainer}>
        <View style={styles.detailCard}>
          <View style={styles.detailHeader}>
            <Text style={styles.detailTitle}>{deal.title}</Text>
            <View style={styles.detailMeta}>
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{getStageInfo(deal.stage).name}</Text>
              </View>
              <Text style={styles.detailValue}>{formatCurrency(deal.value)}</Text>
            </View>
          </View>
        </View>
        <View style={styles.detailSection}>
          <Text style={styles.detailSectionTitle}>Contact</Text>
          {deal.contact_email && (
            <View style={styles.detailContact}>
              <Ionicons name="mail" size={20} color={BRAND.sub} />
              <Text style={styles.contactEmail}>{deal.contact_email}</Text>
            </View>
          )}
          {deal.contact_phone && (
            <View style={styles.detailContact}>
              <Ionicons name="call" size={20} color={BRAND.sub} />
              <Text style={styles.contactPhone}>{deal.contact_phone}</Text>
            </View>
          )}
        </View>
      </ScrollView>
    );
  };

  const renderAddDeal = () => (
    <ScrollView style={styles.formContainer}>
      <View>
        <Text style={styles.formLabel}>Company</Text>
        <TextInput
          style={styles.input}
          placeholder="Enter company"
        />
        {/* ... Form fields ... */}
      </View>
    </ScrollView>
  );

  // 3. "Add" Forms
  const renderAddDealForm = () => (
    <View style={styles.addScreen}>
      {/* Simplified for brevity */}
      <Text>Add Deal Form</Text>
    </View>
  );

  const renderAddProduct = () => (
    <View style={styles.formContainer}>
      <Text style={styles.formLabel}>Add Product Form</Text>
    </View>
  );
  const renderAddTransaction = () => (
    <View style={styles.formContainer}>
      <Text style={styles.formLabel}>Add Transaction Form</Text>
    </View>
  );
  const renderAddEmployee = () => (
    <View style={styles.formContainer}>
      <Text style={styles.formLabel}>Add Employee Form</Text>
    </View>
  );
  const renderAddProduction = () => (
    <View style={styles.formContainer}>
      <Text style={styles.formLabel}>Add Production Form</Text>
    </View>
  );

  // HR Employees List View
  const renderHREmployees = () => {
    const filteredEmployees = getFilteredData();

    return (
      <View style={styles.listContainer}>
        <FlatList
          data={filteredEmployees}
          renderItem={({ item }) => (
            <View style={styles.employeeCard}>
              <View style={styles.employeeAvatar}>
                <Text style={styles.avatarText}>
                  {item.name ? item.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'E'}
                </Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.employeeName}>{item.name}</Text>
                <Text style={styles.employeePosition}>{item.position}</Text>
                <Text style={styles.employeeDepartment}>{item.department}</Text>
              </View>
              <View style={styles.employeeRight}>
                <Text style={styles.employeeSalary}>{formatCurrency(item.salary)}</Text>
                <Text style={[
                  styles.statusBadge,
                  { backgroundColor: item.status === 'active' ? BRAND.success : BRAND.danger }
                ]}>
                  <Text style={styles.statusText}>{item.status ? String(item.status ?? '').toUpperCase() : ''}</Text>
                </Text>
              </View>
            </View>
          )}
          keyExtractor={item => item.id}
          ListEmptyComponent={() => (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="account-group" size={64} color={BRAND.sub} />
              <Text style={styles.emptyStateText}>No employees found</Text>
            </View>
          )}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={moduleConfig.color} />}
        />
      </View>
    );
  };

  // Search Bar Component
  const renderSearchBar = () => (
    <View style={styles.searchBar}>
      <MaterialCommunityIcons name="magnify" color="#999" size={20} />
      <TextInput
        style={styles.searchInput}
        placeholder={`Search ${(screenConfig.title || 'items').toLowerCase()}...`}
        placeholderTextColor="#999"
        value={searchQuery}
        onChangeText={setSearchQuery}
      />
    </View>
  );

  // Switch for main content
  const renderContent = () => {
    try {
      if (isLoading) {
        return <View style={styles.loadingContainer}><ActivityIndicator animating size="large" /></View>;
      }

      switch (moduleId) {
        case 'crm':
          if (screen === 'deals') return renderCRMDeals();
          if (screen === 'dealDetail') return renderDealDetail();
          if (screen === 'addDeal') return renderAddDeal();
          break;
        case 'inventory':
          if (screen === 'products') return renderInventoryProducts();
          if (screen === 'addProduct') return renderAddProduct();
          break;
        case 'finance':
          if (screen === 'transactions') return renderFinanceTransactions();
          if (screen === 'addTransaction') return renderAddTransaction();
          break;
        case 'hr':
          if (screen === 'employees') return renderHREmployees();
          if (screen === 'addEmployee') return renderAddEmployee();
          break;
        case 'production':
          if (screen === 'output') return renderProductionOutput();
          if (screen === 'addProduction') return renderAddProduction();
          break;
        default:
          return <View style={styles.errorContainer}>Module not found.</View>;
      }

      return null;
    } catch (error) {
      console.error("Error rendering content:", error);
      return <View style={styles.errorContainer}>Error rendering.</View>;
    }
  };

  // Main render
  return (
    <View style={styles.container}>
      {/* Gradient Header */}
      <LinearGradient
        colors={[moduleConfig.color, `${moduleConfig.color}DD`]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.headerGradient}
      >
        <View style={styles.headerContent}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <MaterialCommunityIcons name="arrow-back" color="#FFF" size={24} />
          </TouchableOpacity>
          <View style={styles.titleContainer}>
            <MaterialCommunityIcons name={screenConfig.icon || 'information'} color="#FFF" size={24} style={styles.titleIcon} />
            <Text style={styles.headerTitle}>{screenConfig.title}</Text>
          </View>
          <View style={styles.headerActions}>
            <TouchableOpacity
              style={styles.headerAction}
              onPress={() => {
                Alert.alert('Feature', 'This feature is coming soon');
              }}
            >
              <MaterialCommunityIcons name="filter" color="#FFF" size={20} />
            </TouchableOpacity>
          </View>
        </View>
      </LinearGradient>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <MaterialCommunityIcons name="magnify" color={BRAND.sub} size={20} />
          <TextInput
            style={styles.searchInput}
            placeholder={`Search ${(screenConfig.title || 'items').toLowerCase()}...`}
            placeholderTextColor={BRAND.sub}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <MaterialCommunityIcons name="close-circle" color={BRAND.sub} size={20} />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>

      {/* Content */}
      <View style={styles.content}>
        {renderContent()}
      </View>

      {/* Floating Action Button */}
      <TouchableOpacity
        style={[styles.fab, { backgroundColor: moduleConfig.color }]}
        onPress={() => {
          // Example action
          navigation.navigate('ERPModuleDetail', {
            moduleId: moduleId,
            screen: `add${moduleId.charAt(0).toUpperCase() + moduleId.slice(1).slice(0, -1)}`,
          });
        }}
      >
        <MaterialCommunityIcons name="plus" size={28} color="#FFF" />
      </TouchableOpacity>
    </View>
  );
};

// ============================================
// Styles
// ============================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: BRAND.bg,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: BRAND.bg,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: BRAND.sub,
    fontWeight: '500',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  headerGradient: {
    paddingTop: Platform.OS === 'ios' ? 50 : 30,
    paddingBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  backButton: {
    padding: 4,
  },
  titleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 8,
  },
  titleIcon: {
    marginRight: 12,
  },
  headerTitle: {
    color: 'white',
    fontSize: 20,
    fontWeight: '700',
    flex: 1,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  headerAction: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: BRAND.line,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: BRAND.bg,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: BRAND.text,
  },
  content: {
    flex: 1,
  },
  listContainer: {
    flex: 1,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyStateText: {
    fontSize: 16,
    color: BRAND.sub,
    fontWeight: '600',
    marginTop: 16,
  },
  emptyStateSubText: {
    fontSize: 14,
    color: BRAND.sub,
    marginTop: 4,
    textAlign: 'center',
  },
  separator: {
    height: 1,
    backgroundColor: BRAND.line,
    marginHorizontal: 16,
  },
  // Deal Card
  dealCard: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginVertical: 8,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  dealHeader: {
    marginBottom: 12,
  },
  dealTitleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  dealTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: BRAND.text,
    flex: 1,
    marginRight: 8,
  },
  dealSubtitle: {
    fontSize: 14,
    color: BRAND.sub,
  },
  dealValue: {
    marginTop: 8,
  },
  dealValueText: {
    fontSize: 18,
    fontWeight: '800',
    color: BRAND.text,
  },
  // Product Card
  productCard: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginVertical: 8,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    flexDirection: 'row',
    alignItems: 'center',
  },
  productHeader: {
    marginRight: 12,
  },
  productContent: {
    flex: 1,
  },
  productTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: BRAND.text,
    marginBottom: 2,
  },
  productSKU: {
    fontSize: 12,
    color: BRAND.sub,
    marginBottom: 4,
  },
  productMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  productStock: {
    fontSize: 12,
    color: BRAND.success,
  },
  productLowStock: {
    color: BRAND.warning,
  },
  productLocation: {
    fontSize: 12,
    color: BRAND.sub,
  },
  productPrice: {
    fontSize: 16,
    fontWeight: '800',
    color: BRAND.text,
  },
  // Transaction Card
  transactionCard: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginVertical: 8,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    flexDirection: 'row',
    alignItems: 'center',
  },
  transactionTypeIndicator: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: BRAND.bg,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  incomeColor: {
    backgroundColor: `${BRAND.success}20`,
  },
  transactionDetails: {
    flex: 1,
  },
  transactionDescription: {
    fontSize: 15,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 4,
  },
  transactionMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  transactionCategory: {
    fontSize: 12,
    color: BRAND.sub,
  },
  transactionAmount: {
    fontSize: 14,
    fontWeight: '700',
    color: BRAND.text,
  },
  transactionStatus: {
    fontSize: 10,
    color: BRAND.warning,
    fontWeight: '600',
  },
  transactionStatusCompleted: {
    color: BRAND.success,
  },
  transactionDate: {
    fontSize: 11,
    color: BRAND.sub,
  },
  // Employee Card
  employeeCard: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginVertical: 8,
    padding: 16,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  employeeAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: `${BRAND.blue}20`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatarText: {
    fontSize: 20,
    fontWeight: '700',
    color: BRAND.blue,
  },
  employeeName: {
    fontSize: 16,
    fontWeight: '700',
    color: BRAND.text,
    marginBottom: 2,
  },
  employeePosition: {
    fontSize: 14,
    color: BRAND.sub,
    marginBottom: 2,
  },
  employeeDepartment: {
    fontSize: 12,
    color: BRAND.blue,
    backgroundColor: `${BRAND.blue}10`,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  employeeRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  employeeSalary: {
    fontSize: 16,
    fontWeight: '800',
    color: BRAND.text,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
    color: 'white',
  },
  // Production Card
  productionCard: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginVertical: 8,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  productionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  productionMeta: {
    fontSize: 14,
    color: BRAND.sub,
  },
  // Detail View
  detailContainer: {
    flex: 1,
    padding: 16,
  },
  detailCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    marginBottom: 16,
  },
  detailHeader: {
    marginBottom: 16,
  },
  detailTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: BRAND.text,
    marginBottom: 8,
  },
  detailMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  badge: {
    backgroundColor: BRAND.info,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  badgeText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  detailValue: {
    fontSize: 20,
    fontWeight: '800',
    color: BRAND.text,
  },
  detailSection: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  detailSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: BRAND.text,
    marginBottom: 12,
  },
  detailContact: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },
  contactEmail: {
    fontSize: 14,
    color: BRAND.text,
    flex: 1,
  },
  contactPhone: {
    fontSize: 14,
    color: BRAND.text,
    flex: 1,
  },
  // Form Styles
  formContainer: {
    flex: 1,
    padding: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: BRAND.text,
    marginBottom: 8,
  },
  input: {
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: BRAND.line,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 15,
    color: BRAND.text,
    marginBottom: 16,
  },
  // FAB
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  // Error Container
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  addScreen: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
});

export default ERPModuleDetail;